import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../atoms/dialog';
import { Button } from '../atoms/button';
import { LoginForm } from '../molecules/LoginForm';
import { RegisterForm } from '../molecules/RegisterForm';

interface AuthDialogProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'login' | 'register';
  onModeChange: (mode: 'login' | 'register') => void;
  onLogin?: (email: string, password: string) => Promise<void>;
  onRegister?: (name: string, email: string, password: string) => Promise<void>;
  onGoogleAuth?: () => Promise<void>;
  isLoading?: boolean;
  isWeb?: boolean;
  /** Optional callback to navigate to forgot password page */
  onNavigateToForgotPassword?: () => void;
}

/**
 * AuthDialog
 * 
 * Modal dialog for authentication that uses reusable LoginForm and RegisterForm components.
 * The form UI and validation logic is now shared with LoginTemplate and RegisterTemplate.
 * 
 * Architecture:
 * - LoginForm: Handles login form UI and validation
 * - RegisterForm: Handles registration form UI, validation, and password strength
 * - AuthDialog: Provides dialog wrapper, Google auth button, and mode switching
 * 
 * Benefits:
 * - Single source of truth for form UI/logic (shared with templates)
 * - Reduced code duplication
 * - Consistent behavior across dialog and page modes
 * - Easier to maintain and test
 */
export const AuthDialog: React.FC<AuthDialogProps> = ({
  isOpen,
  onClose,
  mode,
  onModeChange,
  onLogin,
  onRegister,
  onGoogleAuth,
  isLoading = false,
  isWeb = true,
  onNavigateToForgotPassword,
}) => {
  const { t } = useTranslation();

  const handleModeSwitch = useCallback(
    (newMode: 'login' | 'register') => {
      onModeChange(newMode);
    },
    [onModeChange]
  );

  const handleGoogleAuth = useCallback(async () => {
    if (onGoogleAuth && !isLoading) {
      await onGoogleAuth();
    }
  }, [onGoogleAuth, isLoading]);

  const handleLogin = useCallback(
    async (email: string, password: string) => {
      if (onLogin) {
        await onLogin(email, password);
      }
    },
    [onLogin]
  );

  const handleRegister = useCallback(
    async (name: string, email: string, password: string) => {
      if (onRegister) {
        await onRegister(name, email, password);
      }
    },
    [onRegister]
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        id="auth-dialog"
        data-testid="auth-dialog"
        className={`${isWeb ? 'w-[420px]' : 'w-[350px]'} gap-0 py-6 px-6`}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-gray-900 text-center w-full justify-center mb-4">
            {mode === 'login' ? 'Sign In' : 'Create Account'}
          </DialogTitle>
        </DialogHeader>

        {/* Reusable Login/Register Forms with Google button */}
        <div className="mt-4">
          {mode === 'login' ? (
            <LoginForm
              onLogin={handleLogin}
              onGoogleAuth={handleGoogleAuth}
              isLoading={isLoading}
              isWeb={isWeb}
              showForgotPassword={true}
              isGoogleButtonTop={true}
              onForgotPassword={onNavigateToForgotPassword}
            />
          ) : (
            <RegisterForm
              onRegister={handleRegister}
              onGoogleAuth={handleGoogleAuth}
              isLoading={isLoading}
              isWeb={isWeb}
              showPrivacyPolicy={true}
              isGoogleButtonTop={true}
            />
          )}
        </div>

        {/* Mode switch footer */}
        <div className="flex items-center justify-center mt-[18px]">
          <p className="text-sm text-gray-600 font-normal">
            {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}{' '}
            <button
              type="button"
              onClick={() => handleModeSwitch(mode === 'login' ? 'register' : 'login')}
              className="text-primary-600 hover:text-primary-700 font-semibold"
              disabled={isLoading}
              data-testid="btn-switch-mode"
            >
              {mode === 'login' ? 'Sign up' : 'Log in'}
            </button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
